import { useEffect, useMemo, useRef } from 'react'
import { Player, type PlayerRef } from '@remotion/player'
import { TextComposition, textCompositionSchema } from 'vibe-remotion'
import type { EditSpecFormatDict } from '../../../../generated/nodeDataTypes'
import { toTextOverlayInput } from './dreamvibeAdapter'
import { TextPreviewControls, useTextPreviewDrag } from './TextPreviewControls'
import type { TextDocument, TextPatch } from './textTrack'

export function LegacyTextPreview({ document, playhead, positionRef, selectedKey, disabled, onSelect, onDraft, onCommit, onGestureStart, duration }: {
  document: TextDocument & { format: EditSpecFormatDict }; playhead: number; positionRef?: React.MutableRefObject<number>
  selectedKey: string | null; disabled: boolean; onSelect?: (key: string) => void
  onDraft?: (key: string, patch: TextPatch | null, revision: number) => void
  onCommit?: (key: string, patch: TextPatch | null, revision: number) => void; onGestureStart?: () => void; duration: number
}) {
  const root = useRef<HTMLDivElement>(null)
  const player = useRef<PlayerRef>(null)
  const props = useMemo(() => textCompositionSchema.parse({ format: document.format, overlays: toTextOverlayInput(document.overlays ?? []),
    durationSeconds: Math.max(1 / document.format.fps, duration), transparent: true }), [document, duration])
  const drag = useTextPreviewDrag({ root, editSpec: document, revision: 0, disabled, onSelect, onDraft, onCommit, onGestureStart })
  useEffect(() => {
    let raf = 0
    const seek = () => {
      const frame = Math.max(0, Math.min(Math.ceil(props.durationSeconds * props.format.fps) - 1, Math.round((positionRef?.current ?? playhead) * props.format.fps)))
      if (player.current?.getCurrentFrame() !== frame) player.current?.seekTo(frame)
      raf = requestAnimationFrame(seek)
    }
    seek()
    return () => cancelAnimationFrame(raf)
  }, [playhead, positionRef, props.durationSeconds, props.format.fps])
  return <div ref={root} {...drag.handlers} className="absolute inset-0 z-10" data-testid="legacy-text-preview">
    <Player ref={player} component={TextComposition} inputProps={props} compositionWidth={props.format.width} compositionHeight={props.format.height}
      fps={props.format.fps} durationInFrames={Math.max(1, Math.ceil(props.durationSeconds * props.format.fps))}
      controls={false} clickToPlay={false} acknowledgeRemotionLicense style={{ width: '100%', height: '100%' }} />
    <TextPreviewControls root={root} selectedKey={selectedKey} disabled={disabled} editSpec={document} playhead={playhead} onBegin={drag.begin} />
  </div>
}
